import { Fragment, ReactNode, memo, useMemo } from "react";

import { ChatPanelToggleButton } from "./ChatPanelToggleButton";
import { HandRaiseButton } from "./HandRaiseButton";
import { ReactionMenu } from "./ReactionMenu";

import { withActiveAv } from "components/av/AvSubsystemLoader";
import { AudioVideoOptionsMenuItems } from "components/footer/AudioVideoOptionsMenu";
import { AIPanelToggleButton } from "components/footer/icon-buttons/AIPanelToggleButton";
import { InstructorNotesPanelToggleButton } from "components/footer/icon-buttons/InstructorNotesPanelToggleButton";
import { UserPanelToggleButton } from "components/footer/icon-buttons/UserPanelToggleButton";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { Menu, MenuDivider, SubMenu } from "mds/components/Menu";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import {
  useIsMdOrLarger,
  useIsSmOrLarger,
  useIsXlOrLarger,
  useIsXsOrLarger,
} from "mds/hooks/use-responsive";
import { ChevronUpIcon, GearSettingsIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  selectActiveClassSessionId,
  selectCanStudentsScreenshare,
  selectClassTimerEndsAt,
  selectCurrentClassUserId,
  selectCurrentClassUserIsInPersonClass,
  selectCurrentPageId,
  selectCurrentSection,
  selectCurrentSectionIsTemplate,
  selectIsDesktopDevice,
  selectScreensharingClassUserId,
} from "store/selectors";
import { selectCurrentBreakoutGroupId, selectIsInBreakout } from "store/selectors/breakouts";
import { selectIsObservingVirtualClass } from "store/selectors/pubsub-virtual-class-state";
import { useObjectSelector } from "store/store-hooks";
import { useAv } from "utils/av";
import { useAutoClearReaction } from "utils/hooks/use-auto-clear-reaction";
import { usePresentation } from "utils/presentation";

const LazyScreenshareButton = withActiveAv(() => import("components/av/ScreenshareButton"));

type ButtonOrMenuItem = { key: string; button: ReactNode; menuItem: ReactNode | null };

/* Users see the following buttons in this order:
 * 1. Hand raise
 * 2. Reactions
 * 3. Chat
 * 4. Screenshare (students) / Instructor notes (instructors)
 * 5. AI assistant
 * 6. Participants
 */
export const IconButtons = memo(() => {
  const currentClassUserId = useAppSelector(selectCurrentClassUserId);
  const { canAuthorSection } = useCapabilities();
  const currentSection = useObjectSelector(selectCurrentSection, ["student_ai_assistant_enabled"]);
  const canSeeAIPanel = canAuthorSection || currentSection?.student_ai_assistant_enabled;
  const screensharingClassUserId = useAppSelector(selectScreensharingClassUserId);
  const canStudentsScreenshare = useAppSelector(selectCanStudentsScreenshare);
  const { isInVirtualClass, isInVirtualClassOrObserving, isVirtualClassActive } = useAv();
  const isInInPersonClass = useAppSelector(selectCurrentClassUserIsInPersonClass);
  const isObservingClass = useAppSelector(selectIsObservingVirtualClass);
  const isInClassSession = isInVirtualClass || isInInPersonClass;
  const activeClassSessionId = useAppSelector(selectActiveClassSessionId);
  const isInTemplateSection = useAppSelector(selectCurrentSectionIsTemplate);
  const isDesktopDevice = useAppSelector(selectIsDesktopDevice);
  const selectedPageId = useAppSelector(selectCurrentPageId);
  const currentBreakoutGroupId = useAppSelector(selectCurrentBreakoutGroupId);

  const isXsOrLarger = useIsXsOrLarger();
  const isSmOrLarger = useIsSmOrLarger();
  const isMdOrLarger = useIsMdOrLarger();
  const isXlOrLarger = useIsXlOrLarger();
  // Instructors do not see mobile version of footer
  const isMobile = !isMdOrLarger && !canAuthorSection;

  // Run here (rather than in ReactionMenu) so the auto-clear timeout survives ReactionMenu
  // unmounting when it's hidden in the mobile caret menu (which uses `unmountOnClose`).
  useAutoClearReaction();

  const isBreakoutActive = useAppSelector(selectIsInBreakout);
  const isBreakoutsBannerVisible = isInVirtualClassOrObserving && isBreakoutActive;
  const { presentedPageId } = usePresentation();
  const isPresentationBannerVisible =
    isInClassSession && (screensharingClassUserId || presentedPageId);
  const endTime = useAppSelector(selectClassTimerEndsAt);
  const isTimerBannerVisible = isInClassSession && endTime;
  // At most two banners will be visible at a time (presentation + timer or presentation + breakouts)
  const visibleBannersCount = [
    isBreakoutsBannerVisible,
    isPresentationBannerVisible,
    isTimerBannerVisible,
  ].filter(Boolean).length;

  // TODO: this has only been tested for students
  // TODO: do in a less hardcoded way?
  const visibleButtonCount = useMemo(() => {
    if (!activeClassSessionId) {
      // At most 3 buttons (Instructor notes, AI assistant, Participants) will be visible when no class session is active
      return 3;
    }

    if (isXlOrLarger) {
      // >= 1280px
      if (canAuthorSection && visibleBannersCount > 1) {
        return 0;
      }
      if (canAuthorSection && visibleBannersCount > 0) {
        return 2;
      }
      return 6;
    }
    if (isMdOrLarger) {
      // >= 850px
      // md and lg breakpoints can be treated the same
      if (canAuthorSection && visibleBannersCount > 0) {
        return 1;
      }
      if (visibleBannersCount > 1) {
        return 2;
      }
      return 6;
    }
    if (isSmOrLarger) {
      // >= 640px
      // Show a max of 8 total buttons on mobile for students
      // Mic, camera, caret menu button, and leave class button take up 4 slots
      if (canAuthorSection && visibleBannersCount > 1) {
        return 0;
      }
      if (visibleBannersCount > 1) {
        return 2;
      }
      if (visibleBannersCount > 0) {
        return 3;
      }
      return 4;
    }
    if (isXsOrLarger) {
      // >= 430px
      if (visibleBannersCount > 1) {
        return 0;
      }
      if (visibleBannersCount > 0) {
        return 1;
      }
      return 2;
    }
    return 0;
  }, [
    isXsOrLarger,
    isSmOrLarger,
    isMdOrLarger,
    isXlOrLarger,
    visibleBannersCount,
    activeClassSessionId,
    canAuthorSection,
  ]);

  const canSeeHandRaiseButton =
    isVirtualClassActive && isInVirtualClass && !(canAuthorSection && currentBreakoutGroupId);
  const canSeeReactionButton = isInClassSession;
  const canSeeChatButton = isInClassSession || isObservingClass;
  // Instructors see screenshare button in a different location (presentation options)
  const canSeeScreenshareButton =
    !canAuthorSection &&
    isInVirtualClass &&
    currentClassUserId !== screensharingClassUserId &&
    canStudentsScreenshare &&
    isDesktopDevice;
  const canSeeInstructorNotesButton = canAuthorSection && selectedPageId;
  const canSeeAIButton = canAuthorSection || canSeeAIPanel;
  const canSeeUserButton = !isInTemplateSection;

  const entries = useMemo<ButtonOrMenuItem[]>(() => {
    const list: ButtonOrMenuItem[] = [];
    if (canSeeHandRaiseButton) {
      list.push({
        key: "hand",
        button: <HandRaiseButton isMobile={isMobile} />,
        menuItem: <HandRaiseButton isMenuItem />,
      });
    }
    if (canSeeReactionButton) {
      list.push({
        key: "reactions",
        button: <ReactionMenu isMobile={isMobile} />,
        menuItem: <ReactionMenu isMenuItem />,
      });
    }
    if (canSeeChatButton) {
      list.push({
        key: "chat",
        button: <ChatPanelToggleButton isMobile={isMobile} />,
        menuItem: <ChatPanelToggleButton isMenuItem />,
      });
    }
    if (canSeeScreenshareButton) {
      list.push({
        key: "screen",
        button: <LazyScreenshareButton isMobile={isMobile} />,
        menuItem: <LazyScreenshareButton isMenuItem />,
      });
    }
    if (canSeeInstructorNotesButton) {
      list.push({
        key: "notes",
        button: <InstructorNotesPanelToggleButton />,
        menuItem: <InstructorNotesPanelToggleButton isMenuItem />,
      });
    }
    if (canSeeAIButton) {
      list.push({
        key: "ai",
        button: <AIPanelToggleButton isMobile={isMobile} />,
        menuItem: <AIPanelToggleButton isMenuItem />,
      });
    }
    if (canSeeUserButton) {
      list.push({
        key: "users",
        button: <UserPanelToggleButton isMobile={isMobile} />,
        menuItem: <UserPanelToggleButton isMenuItem />,
      });
    }
    return list;
  }, [
    canSeeHandRaiseButton,
    canSeeReactionButton,
    canSeeChatButton,
    canSeeScreenshareButton,
    canSeeInstructorNotesButton,
    canSeeAIButton,
    canSeeUserButton,
    isMobile,
  ]);

  const visibleButtons = entries.slice(0, visibleButtonCount);
  const hiddenMenuItems = entries.slice(visibleButtonCount).filter((e) => e.menuItem !== null);

  const MenuButton = (
    <SquareIconButton
      aria-label={t("footer.footer_options")}
      eventName="Footer icon buttons menu"
      kind="tertiary"
      size={isMobile ? "xl" : "l"}
      onClick={(e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <ChevronUpIcon />
    </SquareIconButton>
  );

  return (
    <div className="flex min-w-0 items-center gap-2">
      {visibleButtons.map((entry) => (
        <Fragment key={entry.key}>{entry.button}</Fragment>
      ))}
      {(hiddenMenuItems.length > 0 || (isMobile && isInVirtualClass)) && (
        <Menu
          className="dark-menu"
          direction="top"
          menuButton={MenuButton}
          menuClassName="xs:min-w-[320px] max-xs:!left-3 max-xs:!w-[calc(100vw-24px)]"
          unmountOnClose
        >
          {hiddenMenuItems.map((entry, index) => (
            <Fragment key={entry.key}>
              {entry.menuItem}
              {index < hiddenMenuItems.length - 1 && <MenuDivider />}
            </Fragment>
          ))}
          {isMobile && isInVirtualClass && (
            <>
              {hiddenMenuItems.length > 0 && <MenuDivider />}
              <SubMenu
                label={<IconText iconStart={<GearSettingsIcon />} text={t("av.av_settings")} />}
              >
                <AudioVideoOptionsMenuItems />
              </SubMenu>
            </>
          )}
        </Menu>
      )}
    </div>
  );
});

IconButtons.displayName = "IconButtons";
